import type { FC, ReactNode } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { ErrorBoundary } from "react-error-boundary";
import { useSelector } from "react-redux";

import { ErrorFallback } from "app/components/error-boundary-no-data/ErrorBoundaryEmptyState";
import { PageNotificationBarContainer } from "app/components/React/NotificationBarContainer/PageNotificationBarContainer";
import { InfoBanner } from "app/components/React/PlaResearch/InfoBanner";
import { IndustryAnalysisSubNav } from "app/components/sub-navigation-bar/IndustryAnalysisSubNav";
import useStickySubNav from "app/components/sub-navigation-bar/UseStickySubNav";
import { SwDemoBanner } from "app/components/sw-demo-banner/SwDemoBanner";
import { IndustryResearchPage } from "app/pages/industry-analysis/frame/IndustryResearchPage";
import { DEFAULT_PAGE_LAYOUT_CLASS_NAME } from "app/UtilitiesAndConstants/Constants/css";
import { InfoBannerContainer } from "app/UtilitiesAndConstants/UtilitiesComponents/InfoBannerContainer";

import { CategoriesProvider } from "../categories-context/CategoriesContext";
import { getCategoriesFromParams } from "../common/getCategories";

import "./navigationInterceptor";

/**
 * Main Industry Analysis Route Component
 *
 * Provides CategoriesContext to all child components automatically.
 * Categories are extracted from route parameters - single source of truth.
 */
export const IndustryAnalysisRoute: FC<{ shouldLogErrorFallback: boolean; children?: ReactNode }> = ({
    shouldLogErrorFallback,
    children,
}) => {
    const { state, params } = useCurrentStateAndParams();
    const pageLayoutClassName = state.pageLayoutClassName ?? DEFAULT_PAGE_LAYOUT_CLASS_NAME;
    const { SubNavWrapper, PageContentWrapper, subNavWrapperProps, pageContentWrapperProps } = useStickySubNav();

    // Adding/removing domains from a custom industry updates UserCustomCategoryService's
    // in-memory state and dispatches here, but doesn't necessarily trigger a route
    // transition (e.g. editing from an already-open Overview page). Subscribing forces
    // this component to re-render and recompute categories with the fresh categoryHash,
    // instead of relying on a route param change that may never happen.
    useSelector((state: any) => state.customCategories?.customCategories);

    // Always extract categories from route parameters - single source of truth
    const categories = getCategoriesFromParams(params);

    return (
        <CategoriesProvider categories={categories}>
            <ErrorBoundary
                FallbackComponent={ErrorFallback}
                resetKeys={[state, params]}
            >
                <div className="sw-layout-module-inner">
                    <span
                        className="u-flex-row u-flex-column"
                        style={{ flexGrow: 1, overflowX: "hidden", width: "100%" }}
                    >
                        <SubNavWrapper
                            className="sw-layout-subnav"
                            {...subNavWrapperProps}
                        >
                            <IndustryAnalysisSubNav />
                        </SubNavWrapper>
                        <SwDemoBanner type="basic" />
                        <InfoBanner
                            displayInTab="plaResearch"
                            userDataBannerKey="userDataBannerKeyWAPla"
                            text="keyword.analysis.plaResearch.page.toolTip.text"
                            title="keyword.analysis.plaResearch.page.toolTip.title"
                        />
                        <InfoBannerContainer
                            displayInPage={["organicsearch_websites_v2_traffic_keywords"]}
                            text="analysis.search.keywords.info.banner.text"
                            title="analysis.search.keywords.info.banner.title"
                            displayInDuration={["28d"]}
                            displayInWebSource={["MobileWeb"]}
                        />
                        <PageNotificationBarContainer />
                        <PageContentWrapper
                            className="sw-layout-scrollable-element sw-layout-research use-sticky-css-rendering"
                            {...pageContentWrapperProps}
                        >
                            <div className={`sw-page-websiteAnalysis ${pageLayoutClassName}`}>
                                <section data-automation="page-frame-container">
                                    <section className={"fadeIn"}>
                                        {children != null ? (
                                            children
                                        ) : (
                                            <IndustryResearchPage shouldLogErrorFallback={shouldLogErrorFallback} />
                                        )}
                                    </section>
                                </section>
                            </div>
                        </PageContentWrapper>
                    </span>
                </div>
            </ErrorBoundary>
        </CategoriesProvider>
    );
};
